import React, {
  useState,
  useEffect,
  Suspense,
  useCallback,
  useRef,
} from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axios";
import PropertyDetailView from "./PropertyDetailView";
import {
  FaSearch,
  FaArrowsAlt,
  FaBed,
  FaBath,
  FaArrowLeft,
} from "react-icons/fa";
import { CiLocationOn } from "react-icons/ci";
import { ProgressSpinner } from "primereact/progressspinner";
import PropertyModel from "./Mappropertymodel";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import { useProperties } from "../hooks/useProperty";

// OpenLayers imports
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import VectorSource from "ol/source/Vector";
import { fromLonLat, toLonLat } from "ol/proj";
import {
  Style,
  Icon,
  Circle as CircleStyle,
  Fill,
  Stroke,
  Text,
} from "ol/style";
import { Feature } from "ol";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import Circle from "ol/geom/Circle";
import { defaults as defaultControls, Zoom, ScaleLine } from "ol/control";
import { Draw, Modify, Snap } from "ol/interaction";
import { MdSearch } from "react-icons/md";
import PublicPageShell from "./PublicPageShell";

// Add custom CSS for markers
const customMarkerStyles = `
  .custom-property-marker {
    background: transparent !important;
    border: none !important;
  }
  .custom-property-marker div {
    transition: transform 0.2s ease;
  }
  .custom-property-marker div:hover {
    transform: scale(1.2);
  }
`;

// Inject custom styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = customMarkerStyles;
  document.head.appendChild(styleElement);
}

// Helper function to get marker color based on property status
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "sale":
      return "#003049"; // Dark blue
    case "sold":
      return "#669bbc"; // Light blue
    case "rent":
      return "#780000"; // Dark red
    case "rented":
      return "#c1121f"; // Red
    case "active":
      return "#28a745"; // Green
    case "pending":
      return "#ffc107"; // Yellow
    case "inactive":
      return "#6c757d"; // Gray
    default:
      return "#6c757d"; // Default gray
  }
};

// OpenLayers Map Component
const OpenLayersMap = ({
  properties,
  filteredProperties,
  onMarkerClick,
  getCoordinates,
  onAreaSearch,
}) => {
  const mapRef = useRef();
  const mapInstanceRef = useRef();
  const markerSource = useRef(new VectorSource());
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [drawingMode, setDrawingMode] = useState(null);
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);

  // Separate state for base map and information layers
  const [baseMapMode, setBaseMapMode] = useState("default");
  const [activeInfoLayers, setActiveInfoLayers] = useState([]);

  // Drawing layers and interactions
  const drawingSource = useRef(new VectorSource());
  const drawingLayer = useRef(
    new VectorLayer({
      source: drawingSource.current,
      style: new Style({
        stroke: new Stroke({
          color: "#ff0000",
          width: 2,
        }),
        fill: new Fill({
          color: "rgba(255, 0, 0, 0.1)",
        }),
      }),
    }),
  );

  // Create marker style based on property status
  const createMarkerStyle = (status, isHovered = false) => {
    const color = getStatusColor(status);
    return new Style({
      image: new CircleStyle({
        radius: isHovered ? 16 : 12,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: "white", width: isHovered ? 4 : 3 }),
      }),
      text: new Text({
        text: status?.charAt(0)?.toUpperCase() || "P",
        fill: new Fill({ color: "white" }),
        font: isHovered ? "bold 14px Arial" : "bold 12px Arial",
        offsetY: 1,
      }),
    });
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim() || !mapInstanceRef.current) return;

    try {
      // Nominatim search
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
      );
      const data = await res.json();
      if (data?.[0]) {
        const { lon, lat } = data[0];
        const view = mapInstanceRef.current.getView();
        view.animate({
          center: fromLonLat([parseFloat(lon), parseFloat(lat)]),
          zoom: 12,
          duration: 1000,
        });
        setSearchQuery("");
      }
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  // Clear all drawings
  const clearDrawings = () => {
    if (drawingSource.current) {
      drawingSource.current.clear();
    }
    setDrawingMode(null);
  };

  // Update drawing mode
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remove existing draw interactions
    const interactions = mapInstanceRef.current.getInteractions();
    interactions.forEach((interaction) => {
      if (
        interaction instanceof Draw ||
        interaction instanceof Modify ||
        interaction instanceof Snap
      ) {
        mapInstanceRef.current.removeInteraction(interaction);
      }
    });

    // Only add drawing interactions if drawing mode is active
    if (drawingMode) {
      let drawType = drawingMode;

      // For rectangles, we use Polygon type but handle it specially
      if (drawingMode === "Rectangle") {
        drawType = "Polygon";
      }

      const draw = new Draw({
        source: drawingSource.current,
        type: drawType,
      });

      const modify = new Modify({ source: drawingSource.current });
      const snap = new Snap({ source: drawingSource.current });

      mapInstanceRef.current.addInteraction(draw);
      mapInstanceRef.current.addInteraction(modify);
      mapInstanceRef.current.addInteraction(snap);

      draw.on("drawend", (event) => {
        const feature = event.feature;
        const geometry = feature.getGeometry();

        // Convert geometry to coordinates for area search
        let coordinates;
        let geometryType = drawingMode; // Use the original drawing mode, not the OpenLayers type

        if (geometry instanceof Polygon) {
          coordinates = geometry.getCoordinates()[0];
        } else if (geometry instanceof Circle) {
          const center = geometry.getCenter();
          const radius = geometry.getRadius();
          coordinates = [center, radius];
        }

        if (coordinates && onAreaSearch) {
          onAreaSearch(coordinates, geometryType);
        }

        // Clear drawing mode after completion
        setDrawingMode(null);
      });
    }
  }, [drawingMode, onAreaSearch]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    console.log("Initializing OpenLayers map...");
    console.log("Map container:", mapRef.current);
    console.log("Container dimensions:", {
      width: mapRef.current.offsetWidth,
      height: mapRef.current.offsetHeight,
      clientWidth: mapRef.current.clientWidth,
      clientHeight: mapRef.current.clientHeight,
    });

    // Wait for the container to be properly sized
    const initMap = () => {
      if (!mapRef.current) return;

      console.log("Creating map with container:", mapRef.current);

      const view = new View({
        center: fromLonLat([-81.5158, 27.6648]), // Center of Florida
        zoom: 8,
      });

      // Use more reliable tile sources - start with OpenStreetMap as default
      const defaultLayer = new TileLayer({
        source: new OSM(),
        visible: true,
      });

      const satelliteLayer = new TileLayer({
        source: new XYZ({
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          crossOrigin: "anonymous",
        }),
        visible: false,
      });

      const streetLayer = new TileLayer({
        source: new OSM(),
        visible: false,
      });

      // Add detailed street layer with labels
      const detailedStreetLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          subdomains: ["a", "b", "c"],
        }),
        visible: false,
      });

      // Add satellite with labels layer
      const satelliteWithLabelsLayer = new TileLayer({
        source: new XYZ({
          url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
          crossOrigin: "anonymous",
        }),
        visible: false,
      });

      // Add terrain layer with elevation info - use more reliable source
      const terrainLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
          subdomains: ["a", "b", "c"],
        }),
        visible: false,
      });

      // Add hybrid terrain layer - satellite with terrain overlay
      const hybridTerrainLayer = new TileLayer({
        source: new XYZ({
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
          crossOrigin: "anonymous",
        }),
        visible: false,
      });

      // Add fallback terrain layer using CartoDB
      const terrainFallbackLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
          subdomains: ["a", "b", "c", "d"],
        }),
        visible: false,
      });

      // Add transportation layer - use CartoDB for better reliability
      const transportLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
          subdomains: ["a", "b", "c", "d"],
        }),
        visible: false,
      });

      // Add business/POI layer - use CartoDB for better reliability
      const poiLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
          subdomains: ["a", "b", "c", "d"],
        }),
        visible: false,
      });

      // Add information overlay layer with place names and landmarks
      const infoOverlayLayer = new TileLayer({
        source: new XYZ({
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          subdomains: ["a", "b", "c"],
        }),
        visible: false,
      });

      const markerLayer = new VectorLayer({
        source: markerSource.current,
      });

      const map = new Map({
        target: mapRef.current,
        layers: [
          defaultLayer,
          satelliteLayer,
          streetLayer,
          detailedStreetLayer,
          satelliteWithLabelsLayer,
          terrainLayer,
          hybridTerrainLayer, // Added hybrid terrain layer
          terrainFallbackLayer,
          transportLayer,
          poiLayer,
          infoOverlayLayer, // Added info overlay layer
          drawingLayer.current,
          markerLayer,
        ],
        view,
        controls: defaultControls({ attribution: false }).extend([
          new Zoom(),
          new ScaleLine(),
        ]),
      });

      // Add drawing interactions only when drawing mode is active
      if (drawingMode) {
        let drawType = drawingMode;

        // For rectangles, we use Polygon type but handle it specially
        if (drawingMode === "Rectangle") {
          drawType = "Polygon";
        }

        const draw = new Draw({
          source: drawingSource.current,
          type: drawType,
        });

        const modify = new Modify({ source: drawingSource.current });
        const snap = new Snap({ source: drawingSource.current });

        map.addInteraction(draw);
        map.addInteraction(modify);
        map.addInteraction(snap);

        // Handle drawing completion
        draw.on("drawend", (event) => {
          const feature = event.feature;
          const geometry = feature.getGeometry();

          // Convert geometry to coordinates for area search
          let coordinates;
          let geometryType = drawingMode; // Use the original drawing mode, not the OpenLayers type

          if (geometry instanceof Polygon) {
            coordinates = geometry.getCoordinates()[0];
          } else if (geometry instanceof Circle) {
            const center = geometry.getCenter();
            const radius = geometry.getRadius();
            coordinates = [center, radius];
          }

          if (coordinates && onAreaSearch) {
            onAreaSearch(coordinates, geometryType);
          }

          // Clear drawing mode after completion
          setDrawingMode(null);
        });
      }

      // Set initial layer visibility - start with default layer
      defaultLayer.setVisible(true);
      satelliteLayer.setVisible(false);
      streetLayer.setVisible(false);

      // Add event listeners for debugging
      map.on("loadstart", () => console.log("Map loading started"));
      map.on("loadend", () => console.log("Map loading finished"));
      map.on("rendercomplete", () => console.log("Map render complete"));

      // Handle tile loading errors
      defaultLayer.getSource().on("tileloaderror", () => {
        console.log("Default tiles failed to load");
      });

      satelliteLayer.getSource().on("tileloaderror", () => {
        console.log("Satellite tiles failed to load");
      });

      streetLayer.getSource().on("tileloaderror", () => {
        console.log("Street tiles failed to load");
      });

      mapInstanceRef.current = map;

      // Force a render after a short delay
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.updateSize();
          console.log("Map size updated");

          // Force the view to render
          const view = mapInstanceRef.current.getView();
          view.setCenter(fromLonLat([-81.5158, 27.6648]));
          view.setZoom(8);
        }
      }, 100);

      // Force another render after a longer delay
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.updateSize();
          console.log("Map size updated again");

          // Force the view to render again
          const view = mapInstanceRef.current.getView();
          view.setCenter(fromLonLat([-81.5158, 27.6648]));
          view.setZoom(8);
        }
      }, 500);

      // Final attempt to ensure map renders
      setTimeout(() => {
        if (mapInstanceRef.current) {
          console.log("Final map render attempt");
          mapInstanceRef.current.updateSize();

          // Try to force a render by changing the view slightly
          const view = mapInstanceRef.current.getView();
          const currentCenter = view.getCenter();
          view.setCenter([
            currentCenter[0] + 0.0001,
            currentCenter[1] + 0.0001,
          ]);
          setTimeout(() => {
            view.setCenter(currentCenter);
          }, 50);
        }
      }, 1000);
    };

    // Initialize immediately if container is ready
    if (mapRef.current.offsetWidth > 0) {
      console.log("Container ready, initializing immediately");
      initMap();
    } else {
      console.log("Container not ready, waiting for resize");
      // Wait for container to be sized
      const resizeObserver = new ResizeObserver(() => {
        if (mapRef.current && mapRef.current.offsetWidth > 0) {
          console.log("Container resized, initializing map");
          initMap();
          resizeObserver.disconnect();
        }
      });
      resizeObserver.observe(mapRef.current);

      // Fallback: force initialization after 1 second if resize observer doesn't work
      setTimeout(() => {
        if (!mapInstanceRef.current && mapRef.current) {
          console.log("Fallback: forcing map initialization");
          initMap();
        }
      }, 1000);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined);
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when properties change
  useEffect(() => {
    if (!markerSource.current || !properties) return;

    // Clear existing markers
    markerSource.current.clear();

    // Add new markers
    properties.forEach((property) => {
      const coordinates = getCoordinates(property);
      if (coordinates) {
        const [lon, lat] = coordinates;
        const feature = new Feature({
          geometry: new Point(fromLonLat([lon, lat])),
          property: property, // Store property data in feature
        });

        feature.setStyle(createMarkerStyle(property.cw_listing_status));
        markerSource.current.addFeature(feature);
      }
    });

    // Fit bounds to show all properties
    if (properties.length > 0 && mapInstanceRef.current) {
      const extent = markerSource.current.getExtent();
      if (extent && !extent.every((coord) => coord === Infinity)) {
        const view = mapInstanceRef.current.getView();
        view.fit(extent, {
          padding: [50, 50],
          duration: 1000,
          maxZoom: 15,
        });
      }
    }
  }, [properties]);

  // Handle marker clicks
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const clickHandler = (event) => {
      const feature = mapInstanceRef.current.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature,
      );
      if (feature && feature.get("property")) {
        const property = feature.get("property");
        setSelectedProperty(property);
        setPopupPosition(event.coordinate);
        onMarkerClick(property);
      } else {
        setSelectedProperty(null);
        setPopupPosition(null);
      }
    };

    const pointerMoveHandler = (event) => {
      const feature = mapInstanceRef.current.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature,
      );

      // Reset all markers to normal style
      markerSource.current.getFeatures().forEach((f) => {
        const property = f.get("property");
        if (property) {
          f.setStyle(createMarkerStyle(property.cw_listing_status, false));
        }
      });

      // Highlight hovered marker
      if (feature && feature.get("property")) {
        const property = feature.get("property");
        feature.setStyle(createMarkerStyle(property.cw_listing_status, true));
        mapInstanceRef.current.getTargetElement().style.cursor = "pointer";
      } else {
        mapInstanceRef.current.getTargetElement().style.cursor = "";
      }
    };

    mapInstanceRef.current.on("click", clickHandler);
    mapInstanceRef.current.on("pointermove", pointerMoveHandler);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.un("click", clickHandler);
        mapInstanceRef.current.un("pointermove", pointerMoveHandler);
      }
    };
  }, [onMarkerClick]);

  // Toggle layers
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    console.log("Base map mode:", baseMapMode);
    console.log("Active info layers:", activeInfoLayers);

    const layers = mapInstanceRef.current.getLayers().getArray();
    console.log("Available layers:", layers.length);

    if (layers.length >= 13) {
      const defaultLayer = layers[0]; // OpenStreetMap default
      const satelliteLayer = layers[1]; // ArcGIS satellite
      const streetLayer = layers[2]; // OpenStreetMap street
      const detailedStreetLayer = layers[3]; // Detailed streets
      const satelliteWithLabelsLayer = layers[4]; // Satellite with labels
      const terrainLayer = layers[5]; // Terrain (will be removed)
      const hybridTerrainLayer = layers[6]; // Hybrid terrain
      const terrainFallbackLayer = layers[7]; // Fallback terrain
      const transportLayer = layers[8]; // Transportation
      const poiLayer = layers[9]; // Points of interest
      const infoOverlayLayer = layers[10]; // Info overlay

      // Reset all layers to invisible
      [
        defaultLayer,
        satelliteLayer,
        streetLayer,
        detailedStreetLayer,
        satelliteWithLabelsLayer,
        terrainLayer,
        hybridTerrainLayer,
        terrainFallbackLayer,
        transportLayer,
        poiLayer,
        infoOverlayLayer,
      ].forEach((layer) => {
        layer.setVisible(false);
      });

      // Set base map visibility - only one base map at a time
      if (baseMapMode === "default") {
        defaultLayer.setVisible(true);
        console.log("Default layer activated");
      } else if (baseMapMode === "satellite") {
        satelliteLayer.setVisible(true);
        console.log("Satellite layer activated");
      } else if (baseMapMode === "satellite-labels") {
        satelliteWithLabelsLayer.setVisible(true);
        console.log("Satellite with labels layer activated");
      } else if (baseMapMode === "hybrid-terrain") {
        hybridTerrainLayer.setVisible(true);
        console.log("Hybrid terrain layer activated");
      }

      // Set additional information layers - these work WITH the base map
      activeInfoLayers.forEach((layerType) => {
        if (layerType === "detailed") {
          detailedStreetLayer.setVisible(true);
          console.log("Detailed streets layer activated");
        }
        if (layerType === "transport") {
          transportLayer.setVisible(true);
          console.log("Transport layer activated");
        }
        if (layerType === "poi") {
          poiLayer.setVisible(true);
          console.log("POI layer activated");
        }
        if (layerType === "info") {
          infoOverlayLayer.setVisible(true);
          console.log("Info overlay layer activated");
        }
      });

      // Force map update
      mapInstanceRef.current.updateSize();
    } else {
      console.log("Not enough layers available for toggle");
    }
  }, [baseMapMode, activeInfoLayers]);

  return (
    <div className="relative w-full lg:h-[40rem] h-[30rem] rounded-md overflow-hidden border border-gray-300">
      {/* Search Bar */}
      <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-10 w-[200px] lg:w-[300px]">
        <div className="flex bg-white border border-gray-300 rounded shadow">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 px-2 py-1 outline-none text-sm"
            placeholder="Search location..."
          />
          <button
            onClick={handleSearch}
            className="px-3 bg-red-600 text-white hover:bg-red-700 rounded-r"
          >
            <MdSearch />
          </button>
        </div>
      </div>

      {/* Advanced Drawing Tools */}
      <div className="absolute top-3 left-10 z-10 flex flex-col gap-2">
        <button
          className={`border border-gray-300 rounded-lg p-2 shadow hover:bg-gray-100 ${drawingMode === "Polygon"
              ? "bg-blue-100 border-blue-500"
              : "bg-white"
            }`}
          onClick={() =>
            setDrawingMode(drawingMode === "Polygon" ? null : "Polygon")
          }
          title="Draw custom area to search properties"
        >
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </button>
        <button
          className={`border border-gray-300 rounded-lg p-2 shadow hover:bg-gray-100 ${drawingMode === "Rectangle"
              ? "bg-blue-100 border-blue-500"
              : "bg-white"
            }`}
          onClick={() =>
            setDrawingMode(drawingMode === "Rectangle" ? null : "Rectangle")
          }
          title="Draw rectangle to search properties"
        >
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
        </button>
        <button
          className={`border border-gray-300 rounded-lg p-2 shadow hover:bg-gray-100 ${drawingMode === "Circle"
              ? "bg-blue-100 border-blue-500"
              : "bg-white"
            }`}
          onClick={() =>
            setDrawingMode(drawingMode === "Circle" ? null : "Circle")
          }
          title="Draw circle to search properties"
        >
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
            />
          </svg>
        </button>
        <button
          className="bg-white border border-gray-300 rounded-lg p-2 shadow hover:bg-gray-100"
          onClick={clearDrawings}
          title="Clear all drawings"
        >
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Layer Toggle */}
      <div className="absolute top-3 right-3 z-10">
        <button
          className="bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100"
          onClick={() => setLayerPanelOpen(!layerPanelOpen)}
          aria-label="Toggle layer panel"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </button>
      </div>

      {/* Advanced Layer Control Panel */}
      {layerPanelOpen && (
        <div className="absolute top-16 right-3 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-xs">
          <div className="text-sm font-semibold text-gray-700 mb-3">
            Map Layers
          </div>

          {/* Base Maps */}
          <div className="mb-3">
            <div className="text-xs font-medium text-gray-600 mb-2">
              Base Maps
            </div>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="base-map"
                  checked={baseMapMode === "default"}
                  onChange={() => setBaseMapMode("default")}
                  className="form-radio text-blue-600 mr-2"
                />
                <span className="text-xs">Streets & Labels</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="base-map"
                  checked={baseMapMode === "satellite"}
                  onChange={() => setBaseMapMode("satellite")}
                  className="form-radio text-blue-600 mr-2"
                />
                <span className="text-xs">Satellite</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="base-map"
                  checked={baseMapMode === "satellite-labels"}
                  onChange={() => setBaseMapMode("satellite-labels")}
                  className="form-radio text-blue-600 mr-2"
                />
                <span className="text-xs">Satellite + Labels</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="base-map"
                  checked={baseMapMode === "hybrid-terrain"}
                  onChange={() => setBaseMapMode("hybrid-terrain")}
                  className="form-radio text-blue-600 mr-2"
                />
                <span className="text-xs">Hybrid Terrain</span>
              </label>
            </div>
          </div>

          {/* Information Layers */}
          <div className="mb-3">
            <div className="text-xs font-medium text-gray-600 mb-2">
              Information
            </div>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeInfoLayers.includes("detailed")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setActiveInfoLayers((prev) => [...prev, "detailed"]);
                    } else {
                      setActiveInfoLayers((prev) =>
                        prev.filter((layer) => layer !== "detailed"),
                      );
                    }
                  }}
                  className="form-checkbox text-blue-600 mr-2"
                />
                <span className="text-xs">Detailed Streets</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeInfoLayers.includes("transport")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setActiveInfoLayers((prev) => [...prev, "transport"]);
                    } else {
                      setActiveInfoLayers((prev) =>
                        prev.filter((layer) => layer !== "transport"),
                      );
                    }
                  }}
                  className="form-checkbox text-blue-600 mr-2"
                />
                <span className="text-xs">Transportation</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeInfoLayers.includes("poi")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setActiveInfoLayers((prev) => [...prev, "poi"]);
                    } else {
                      setActiveInfoLayers((prev) =>
                        prev.filter((layer) => layer !== "poi"),
                      );
                    }
                  }}
                  className="form-checkbox text-blue-600 mr-2"
                />
                <span className="text-xs">Places & Businesses</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeInfoLayers.includes("info")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setActiveInfoLayers((prev) => [...prev, "info"]);
                    } else {
                      setActiveInfoLayers((prev) =>
                        prev.filter((layer) => layer !== "info"),
                      );
                    }
                  }}
                  className="form-checkbox text-blue-600 mr-2"
                />
                <span className="text-xs">Info Overlay</span>
              </label>
            </div>

            {/* Reset Info Layers Button */}
            {activeInfoLayers.length > 0 && (
              <button
                onClick={() => setActiveInfoLayers([])}
                className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Clear All Info Layers
              </button>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={() => setLayerPanelOpen(false)}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Reset Search Button */}
      <div className="absolute top-[15%] right-3 z-10">
        <button
          className="bg-green-500 text-white text-xs px-3 py-2 rounded shadow hover:bg-green-600"
          onClick={() => {
            // Clear any drawings
            if (drawingSource.current) {
              drawingSource.current.clear();
            }
            setDrawingMode(null);
            // Call parent function to reset search
            if (onAreaSearch) {
              onAreaSearch(null, "reset");
            }
          }}
          title="Reset search and show all properties"
        >
          Reset Search
        </button>
      </div>

      {/* Debug Button - Force Map Init */}
      <div className="absolute top-16 right-3 z-10">
        <button
          className="bg-red-500 text-white text-xs px-2 py-1 rounded shadow hover:bg-red-600"
          onClick={() => {
            if (mapRef.current && !mapInstanceRef.current) {
              console.log("Manual map initialization triggered");
              const initMap = () => {
                // Copy the initMap function logic here
                if (!mapRef.current) return;

                const view = new View({
                  center: fromLonLat([-81.5158, 27.6648]),
                  zoom: 8,
                });

                const defaultLayer = new TileLayer({
                  source: new OSM(),
                  visible: true,
                });

                const map = new Map({
                  target: mapRef.current,
                  layers: [defaultLayer],
                  view,
                  controls: defaultControls({ attribution: false }).extend([
                    new Zoom(),
                    new ScaleLine(),
                  ]),
                });

                mapInstanceRef.current = map;
                map.updateSize();
              };
              initMap();
            }
          }}
        >
          Force Map
        </button>
      </div>

      {/* Drawing Mode Status */}
      {drawingMode && (
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-20 bg-blue-500 text-white px-4 py-2 rounded-lg shadow pointer-events-none">
          <span className="text-sm font-semibold">
            Drawing {drawingMode} -{" "}
            {drawingMode === "Rectangle"
              ? "Click and drag to create rectangle"
              : "Click on map to draw"}
          </span>
        </div>
      )}

      {/* Active Layers Status */}
      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-20 bg-white border border-gray-300 rounded-lg shadow px-3 py-2 pointer-events-none">
        <div className="text-xs text-gray-700">
          <div>
            <strong>Base:</strong> {baseMapMode.replace("-", " ")}
          </div>
          {activeInfoLayers.length > 0 && (
            <div>
              <strong>Info:</strong> {activeInfoLayers.join(", ")}
            </div>
          )}
        </div>
      </div>

      {/* Property Counter - Shows filtered results
      <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-10 bg-white border border-gray-300 rounded-lg shadow px-3 py-2 pointer-events-none">
        <span className="text-sm font-semibold text-gray-700">
          {filteredProperties?.length || 0} Properties
        </span>
        {filteredProperties?.length !== properties?.length && (
          <span className="text-xs text-gray-500 ml-2">
            (of {properties?.length || 0})
          </span>
        )}
      </div> */}

      {/* Map Display */}
      <div
        ref={mapRef}
        className="w-full bg-gray-100 lg:h-[40rem] h-[30rem]"
        style={{
          position: "relative",
          zIndex: 1,
        }}
      />

      {/* Scale and Coordinates Info */}
      <div className="absolute bottom-3 right-3 z-10 bg-white border border-gray-300 rounded-lg shadow px-3 py-2 text-xs text-gray-700">
        <div className="font-semibold mb-1">Map Info</div>
        <div>
          Zoom: <span id="zoom-level">8</span>
        </div>
        <div>
          Center: <span id="map-center">27.66°N, 81.52°W</span>
        </div>
      </div>

      {/* Loading Indicator */}
      {!mapInstanceRef.current && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-0">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading map...</p>
          </div>
        </div>
      )}

      {/* User Instructions */}
      {/* <div className="absolute bottom-20 left-3 z-10 bg-white border border-gray-300 rounded-lg shadow p-3 max-w-xs">
        <div className="text-xs font-semibold text-gray-700 mb-2">Advanced Map Search</div>
        <div className="text-xs text-gray-600 space-y-1">
          <div>• <strong>Draw Area:</strong> Click drawing tool, then click on map</div>
          <div>• <strong>Polygon:</strong> Draw custom area</div>
          <div>• <strong>Rectangle:</strong> Draw rectangular area</div>
          <div>• <strong>Circle:</strong> Draw circular area</div>
          <div>• <strong>Results:</strong> Properties in drawn area</div>
        </div>
      </div> */}

      {/* Legend */}
      {/* <div className="absolute bottom-3 left-3 z-10 bg-white border border-gray-300 rounded-lg shadow p-3">
        <div className="text-xs font-semibold text-gray-700 mb-2">Property Status</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#003049]"></div>
            <span className="text-xs">Sale</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#669bbc]"></div>
            <span className="text-xs">Sold</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#780000]"></div>
            <span className="text-xs">Rent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#c1121f]"></div>
            <span className="text-xs">Rented</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#28a745]"></div>
            <span className="text-xs">Active</span>
          </div>
        </div>
      </div> */}

      {/* Popup */}
      {selectedProperty && popupPosition && (
        <div
          className="absolute bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-xs z-20"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <button
            onClick={() => {
              setSelectedProperty(null);
              setPopupPosition(null);
            }}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
          <PropertyModel property={selectedProperty} />
        </div>
      )}
    </div>
  );
};

const PropertyMapSearchInner = () => {
  const { pageHeader, isLoading } = usePageHeaderByKey("mapsearch");
  const { properties, loading, error } = useProperties();
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("map");
  const [propertyImages, setPropertyImages] = useState({});
  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState(null);
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Helper function to get coordinates from property
  const getCoordinates = (property) => {
    if (!property?.coordinates) {
      return null;
    }

    try {
      let coords;

      if (typeof property.coordinates === "string") {
        // Split by comma and clean up whitespace
        coords = property.coordinates
          .split(",")
          .map((coord) => parseFloat(coord.trim()));
      } else if (Array.isArray(property.coordinates)) {
        // If coordinates is already an array
        coords = property.coordinates.map((coord) => parseFloat(coord));
      } else {
        return null;
      }

      // Simplified validation - just check if we have 2 numbers
      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        return coords;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error parsing coordinates:", error, property.coordinates);
      return null;
    }
  };

  console.log("Properties loaded:", properties?.length);
  console.log("Filtered properties:", filteredProperties?.length);

  // Simple initialization of filteredProperties
  useEffect(() => {
    if (properties && properties.length > 0) {
      const validProperties = properties.filter(
        (property) => getCoordinates(property) !== null,
      );
      setFilteredProperties(validProperties);
    }
  }, [properties]);

  useEffect(() => {
    const fetchImages = async () => {
      const imageRequests = properties.map(async (property) => {
        try {
          const response = await axiosInstance.get(
            `/api/images/${property.id}`,
          );
          return {
            id: property.id,
            imageUrl: response.data[0]?.image_url || "/default-property.jpg",
          };
        } catch (error) {
          console.error(
            "Error fetching image for property ID:",
            property.id,
            error,
          );
          return {
            id: property.id,
            imageUrl: "/default-property.jpg",
          };
        }
      });

      const images = await Promise.all(imageRequests);
      const imageMap = images.reduce(
        (acc, { id, imageUrl }) => ({ ...acc, [id]: imageUrl }),
        {},
      );
      setPropertyImages(imageMap);
    };

    if (properties && properties.length > 0) {
      fetchImages();
    }
  }, [properties]);

  const handleMarkerClick = (property) => {
    setSelectedProperty([
      { label: "City", value: property.city },
      { label: "State", value: property.state },
      { label: "Price", value: `$${property.price_or_rent}` },
      { label: "Bedrooms", value: property.bedrooms },
      { label: "Bathrooms", value: property.bathrooms },
      { label: "ID", value: property.id },
    ]);
  };

  // Handle area search from map drawings
  const handleAreaSearch = (coordinates, geometryType) => {
    console.log("Area search triggered:", { coordinates, geometryType });

    // Handle reset case
    if (geometryType === "reset") {
      const validProperties = properties.filter(
        (property) => getCoordinates(property) !== null,
      );
      setFilteredProperties(validProperties);
      setViewMode("map");
      return;
    }

    // Filter properties within the drawn area
    const propertiesInArea = properties.filter((property) => {
      const propertyCoords = getCoordinates(property);
      if (!propertyCoords) return false;

      // Convert property coordinates to OpenLayers format
      const propertyPoint = fromLonLat(propertyCoords);

      if (geometryType === "Polygon") {
        // Check if point is inside polygon
        const polygon = new Polygon([coordinates]);
        return polygon.intersectsCoordinate(propertyPoint);
      } else if (geometryType === "Circle") {
        // Check if point is inside circle
        const [center, radius] = coordinates;
        const circle = new Circle(center, radius);
        return circle.intersectsCoordinate(propertyPoint);
      } else if (geometryType === "Rectangle") {
        // Check if point is inside rectangle (Rectangle creates a Polygon)
        const polygon = new Polygon([coordinates]);
        return polygon.intersectsCoordinate(propertyPoint);
      }

      return false;
    });

    console.log("Properties in area:", propertiesInArea.length);
    setFilteredProperties(propertiesInArea);
    setViewMode("list");
  };

  const initialFilters = {
    status: "",
    propertyType: "",
    propertySubType: "",
    minPrice: "",
    maxPrice: "",
    minBeds: "",
    maxBeds: "",
    minBaths: "",
    maxBaths: "",
    minGarageSpaces: "",
    maxGarageSpaces: "",
    minArea: "",
    maxArea: "",
    minYearBuilt: "",
    maxYearBuilt: "",
    waterFront: "",
    pool: "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const applyFilters = useCallback(() => {
    // Only apply filters if there's an active search or filter
    const hasActiveFilters =
      searchTerm.trim() || Object.values(filters).some((value) => value !== "");

    if (!hasActiveFilters) {
      const validProperties = properties.filter(
        (property) => getCoordinates(property) !== null,
      );
      setFilteredProperties(validProperties);
      return;
    }

    const filtered = properties.filter((property) => {
      const matchesSearchTerm = searchTerm.trim()
        ? (property.city || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (property.state || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (property.zip_code || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (property.street_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (property.mls_number || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (property.unitNumber || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        : true;

      const matchesFilters =
        (filters.status
          ? property.cw_listing_status?.toLowerCase() ===
          filters.status.toLowerCase()
          : true) &&
        (filters.propertyType
          ? filters.propertyType === "residential"
            ? property.listing_type?.toLowerCase().includes("residential")
            : property.listing_type?.toLowerCase() ===
            filters.propertyType.toLowerCase()
          : true) &&
        (filters.propertySubType
          ? property.home_type
            ? property.home_type.toLowerCase() ===
            filters.propertySubType.toLowerCase()
            : true
          : true) &&
        (filters.minPrice
          ? Number(property.price_or_rent) >= Number(filters.minPrice)
          : true) &&
        (filters.maxPrice
          ? Number(property.price_or_rent) <= Number(filters.maxPrice)
          : true) &&
        (filters.minBeds
          ? Number(property.bedrooms) >= Number(filters.minBeds)
          : true) &&
        (filters.maxBeds
          ? Number(property.bedrooms) <= Number(filters.maxBeds)
          : true) &&
        (filters.minBaths
          ? Number(property.bathrooms) >= Number(filters.minBaths)
          : true) &&
        (filters.maxBaths
          ? Number(property.bathrooms) <= Number(filters.maxBaths)
          : true) &&
        (filters.minArea
          ? Number(property.square_feet) >= Number(filters.minArea)
          : true) &&
        (filters.maxArea
          ? Number(property.square_feet) <= Number(filters.maxArea)
          : true) &&
        (filters.minYearBuilt
          ? Number(property.year_built) >= Number(filters.minYearBuilt)
          : true) &&
        (filters.maxYearBuilt
          ? Number(property.year_built) <= Number(filters.maxYearBuilt)
          : true) &&
        (filters.waterFront
          ? filters.waterFront === "yes"
            ? property.waterfront?.toLowerCase() === "yes"
            : property.waterfront?.toLowerCase() !== "yes"
          : true) &&
        (filters.pool
          ? filters.pool === "yes"
            ? property.private_pool === true
            : property.private_pool !== true
          : true);

      const matches = matchesSearchTerm && matchesFilters;
      if (!matches) {
        console.log(`Property ${property.id} filtered out:`, {
          city: property.city,
          state: property.state,
          status: property.cw_listing_status,
          type: property.listing_type,
          searchTermMatch:
            !searchTerm.trim() ||
            (property.city || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (property.state || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
          filtersMatch: matchesFilters,
        });
      }
      return matches;
    });

    setFilteredProperties(filtered);
    setViewMode(filtered.length > 0 ? "list" : "map");
  }, [filters, properties, searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleViewProperty = (index) => {
    setSelectedPropertyIndex(index);
    setViewMode("detail");
    setViewMode("list");
  };

  const handleNextProperty = () => {
    if (
      selectedPropertyIndex !== null &&
      selectedPropertyIndex < filteredProperties.length - 1
    ) {
      setSelectedPropertyIndex(selectedPropertyIndex + 1);
    }
  };

  const handlePrevProperty = () => {
    if (selectedPropertyIndex !== null && selectedPropertyIndex > 0) {
      setSelectedPropertyIndex(selectedPropertyIndex - 1);
    }
  };

  const handleCloseDetails = () => {
    setSelectedPropertyIndex(null);
    setViewMode("list");
  };

  const handleBackToList = () => {
    setViewMode("list");
  };

  const handleBackToMap = () => {
    setViewMode("map");
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  if (loading || isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ProgressSpinner />
      </div>
    );
  }

  if (error) {
    return <p className="text-center text-red-500">{error}</p>;
  }

  return (
    <PublicPageShell>
      <div className="flex flex-col justify-center w-full">
        <h1 className="text-2xl mb-4 text-center font-bold text-black uppercase">
          {pageHeader?.h1 || "MAP SEARCH"}
        </h1>

        <div className="flex flex-col md:flex-row gap-2 mb-4">
          <form
            onSubmit={handleSearch}
            className="flex flex-col md:flex-row w-full gap-2"
          >
            <input
              type="text"
              placeholder="Enter: City, zip code, address, MLS #, etc."
              className="w-2xl md:w-auto px-4 py-2 border rounded-md border-gray-200 text-gray-700"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              className="bg-orange-600 hover:bg-blue-900 cursor-pointer w-full md:w-52 text-white px-2 py-2 flex items-center justify-center gap-2"
            >
              <FaSearch /> SEARCH
            </button>

            {viewMode === "map" ? (
              <button
                type="button"
                onClick={handleBackToList}
                className="bg-orange-600 hover:bg-blue-900 cursor-pointer w-full md:w-52 text-white px-2 py-2 flex items-center justify-center gap-2"
              >
                LISTINGS
              </button>
            ) : (
              <button
                type="button"
                onClick={handleBackToMap}
                className="bg-orange-600 hover:bg-blue-900 cursor-pointer w-full md:w-52 text-white px-2 py-2 flex items-center justify-center gap-2"
              >
                FILTERS
              </button>
            )}

            {/* Uncomment if needed */}
            {/* {session?.user && (
      <button
        type="button"
        onClick={handleSaveSearch}
        className="bg-orange-600 hover:bg-blue-900 cursor-pointer w-full md:w-52 text-white px-2 py-2 flex items-center justify-center gap-2"
      >
        Save This Search
      </button>
    )} */}
          </form>
        </div>

        <div className="grid lg:grid-flow-col lg:grid-cols-3 grid-cols-1 h-full lg:gap-5 mb-4">
          {/* Left Panel - Filters/List */}
          {viewMode !== "detail" && (
            <motion.div
              className="lg:w-full md:w-full p-2 lg:h-[40rem] h-[30rem] md:mb-5 mb-0 border-2 border-gray-200 rounded-md overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {viewMode === "map" ? (
                <div className="rounded-lg shadow-lg w-full overflow-auto">
                  <div className="bg-white p-4 w-full">
                    <div className="flex flex-col gap-4 mt-2">
                      <div className="relative w-full">
                        <select
                          name="status"
                          id="status"
                          className="w-full p-2 pr-8 border border-gray-300 rounded appearance-none bg-white"
                          value={filters.status}
                          onChange={handleFilterChange}
                        >
                          <option value="">Status</option>
                          <option value="active">Active</option>
                          <option value="activeUnderContract">
                            Active under contract
                          </option>
                          <option value="canceled">Canceled</option>
                          <option value="closed">Closed</option>
                          <option value="expired">Expired</option>
                          <option value="pending">Pending</option>
                          <option value="withdrawn">Withdrawn</option>
                        </select>
                        {/* <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div> */}
                      </div>
                      <div className="relative w-full">
                        <select
                          name="home_type"
                          id="home_type"
                          className="w-full p-2 pr-8 border border-gray-300 rounded appearance-none bg-white"
                          value={filters.home_type}
                          onChange={handleFilterChange}
                        >
                          <option value="">Property type</option>
                          <option value="SFH">SFH</option>
                          <option value="Condo">Condo</option>
                          <option value="Townhome">Townhome</option>
                          <option value="Villa">Villa</option>
                          <option value="Coach Home">Coach Home</option>
                          <option value="Mobile Home">Mobile Home</option>
                          <option value="Apartment Unit">Apartment Unit</option>
                        </select>
                        {/* <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div> */}
                      </div>
                      <div className="relative w-full">
                        <select
                          name="propertySubType"
                          id="propertySubType"
                          className="w-full p-2 pr-8 border border-gray-300 rounded appearance-none bg-white"
                          value={filters.propertySubType}
                          onChange={handleFilterChange}
                        >
                          <option value="">Property sub Type</option>
                          <option value="singleFamilyResidence">
                            Single Family Residence
                          </option>
                          <option value="condo">Condo</option>
                          <option value="townhouse">Townhouse</option>
                          <option value="multiFamily">Multi Family</option>
                        </select>
                        {/* <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div> */}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minPrice"
                          placeholder="Min Price"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minPrice}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxPrice"
                          placeholder="Max Price"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxPrice}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minBeds"
                          placeholder="Min Beds"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minBeds}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxBeds"
                          placeholder="Max Beds"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxBeds}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minBaths"
                          placeholder="Min Baths"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minBaths}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxBaths"
                          placeholder="Max Baths"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxBaths}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minGarageSpaces"
                          placeholder="Min Garage Spaces"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minGarageSpaces}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxGarageSpaces"
                          placeholder="Max Garage Spaces"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxGarageSpaces}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minArea"
                          placeholder="Min Area"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minArea}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxArea"
                          placeholder="Max Area"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxArea}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="minYearBuilt"
                          placeholder="Min Year Built"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.minYearBuilt}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <input
                          type="text"
                          name="maxYearBuilt"
                          placeholder="Max Year Built"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={filters.maxYearBuilt}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="relative w-full">
                        <select
                          name="waterFront"
                          id="waterFront"
                          className="w-full p-2 pr-8 border border-gray-300 rounded appearance-none bg-white"
                          value={filters.waterFront}
                          onChange={handleFilterChange}
                        >
                          <option value="">Waterfront</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                        <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="relative w-full">
                        <select
                          name="pool"
                          id="pool"
                          className="w-full p-2 pr-8 border border-gray-300 rounded appearance-none bg-white"
                          value={filters.pool}
                          onChange={handleFilterChange}
                        >
                          <option value="">Private Pool</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                        <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="w-full flex items-end justify-end mt-4">
                      <button
                        onClick={() =>
                          setFilters({
                            status: "active",
                            propertyType: "residential",
                            propertySubType: "singleFamilyResidence",
                            minPrice: "",
                            maxPrice: "",
                            minBeds: "",
                            maxBeds: "",
                            minBaths: "",
                            maxBaths: "",
                            minGarageSpaces: "",
                            maxGarageSpaces: "",
                            minArea: "",
                            maxArea: "",
                            minYearBuilt: "",
                            maxYearBuilt: "",
                            waterFront: "",
                            pool: "",
                          })
                        }
                        className="bg-red-500 text-white px-4 py-2 rounded"
                      >
                        Reset Filters
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="px-4 sm:px-6">
                  <h2 className="text-sm font-bold mb-4 text-red-500 text-center sm:text-left">
                    Click on property to view detail
                  </h2>

                  <div className="space-y-4">
                    {filteredProperties.map((property, index) => (
                      <div
                        key={property.id}
                        className={`p-2 sm:p-2 border rounded-lg hover:shadow-md transition-shadow bg-white ${selectedPropertyIndex === index
                            ? "border-blue-500 border-2"
                            : "border-gray-200"
                          }`}
                        onClick={() => handleViewProperty(index)}
                      >
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <img
                            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80"
                            className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-lg"
                            alt="property"
                            width={96}
                            height={96}
                            loading="lazy"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-start flex-wrap gap-y-2">
                              <div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                                  {property.title || property.city}
                                </h3>
                                <div className="text-xs sm:text-sm text-gray-500 mb-1">
                                  <span>(Reviews)</span>
                                </div>
                                <p className="text-red-600 font-bold text-base sm:text-lg">
                                  $
                                  {Number(
                                    property.price_or_rent || 0,
                                  ).toLocaleString("en-US")}
                                </p>
                              </div>
                            </div>

                            <div className="flex justify-end-safe mt-2">
                              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                                <FaBed /> {property.bedrooms} Beds
                                <FaBath /> {property.bathrooms} Baths
                                <FaArrowsAlt /> {property.square_feet}
                              </div>
                            </div>

                            <hr className="my-2 border-gray-200" />

                            <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                              <div className="flex items-center gap-2 text-sm">
                                <CiLocationOn /> {property.city}
                              </div>
                              <button
                                onClick={() => handleViewProperty(index)}
                                className="bg-teal-500 text-white px-3 py-1 rounded text-sm hover:bg-teal-600 transition-colors w-full sm:w-auto"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Right Panel - Map or Property Detail */}
          <motion.div
            className={`${viewMode === "detail" ? "col-span-3" : "col-span-2"
              } relative`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {selectedPropertyIndex !== null && (
              <div className=" border-l border-gray-200">
                <PropertyDetailView
                  property={filteredProperties[selectedPropertyIndex]}
                  propertyImages={propertyImages}
                  onClose={handleCloseDetails}
                  onNext={handleNextProperty}
                  onPrev={handlePrevProperty}
                  currentIndex={selectedPropertyIndex}
                  totalProperties={filteredProperties.length}
                />
              </div>
            )}

            <div
              className={`${selectedPropertyIndex !== null ? "col-span-2" : "col-span-3"
                }`}
            >
              <OpenLayersMap
                properties={properties}
                filteredProperties={filteredProperties}
                onMarkerClick={handleMarkerClick}
                getCoordinates={getCoordinates}
                onAreaSearch={handleAreaSearch}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </PublicPageShell>
  );
};

const PropertyMapSearch = () => (
  <Suspense
    fallback={
      <div>
        <ProgressSpinner />
      </div>
    }
  >
    <PropertyMapSearchInner />
  </Suspense>
);

export default PropertyMapSearch;
